import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { subscriptionAPI, venuesAPI, historyAPI } from '../api';

export default function DashboardHome() {
  const [subscription, setSubscription] = useState(null);
  const [venues, setVenues] = useState([]);
  const [recentGames, setRecentGames] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [subRes, venuesRes, historyRes] = await Promise.all([
        subscriptionAPI.getCurrent(),
        venuesAPI.getAll(),
        historyAPI.getAll(5, 0), // Get 5 most recent games
      ]);
      
      setSubscription(subRes.data.subscription);
      setVenues(venuesRes.data.venues);
      setRecentGames(historyRes.data.history);
    } catch (error) {
      console.error('Error fetching dashboard data:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="text-xl text-gray-600">Loading...</div>
      </div>
    );
  }

  const gamesRemaining = subscription?.games_per_month === -1 
    ? 'Unlimited' 
    : subscription?.games_per_month - subscription?.games_used_this_month;

  return (
    <div className="p-8">
      <h1 className="text-3xl font-bold text-gray-800 mb-8">Dashboard</h1>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
        {/* Venues Card */}
        <div className="bg-white rounded-xl shadow-md p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-gray-700">Venues</h3>
            <span className="text-3xl">🏢</span>
          </div>
          <p className="text-4xl font-bold text-teal-600">{venues.length}</p>
          <Link to="/dashboard/venues" className="text-sm text-teal-600 hover:underline mt-2 inline-block">
            Manage Venues →
          </Link>
        </div>

        {/* Games This Month Card */}
        <div className="bg-white rounded-xl shadow-md p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-gray-700">Games Used</h3>
            <span className="text-3xl">🎮</span>
          </div>
          <p className="text-4xl font-bold text-orange-500">
            {subscription?.games_used_this_month || 0}
            <span className="text-lg text-gray-500">
              /{subscription?.games_per_month === -1 ? '∞' : subscription?.games_per_month}
            </span>
          </p>
          <p className="text-sm text-gray-600 mt-2">{gamesRemaining} remaining</p>
        </div>

        {/* Subscription Card */}
        <div className="bg-white rounded-xl shadow-md p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-gray-700">Plan</h3>
            <span className="text-3xl">💳</span>
          </div>
          <p className="text-2xl font-bold text-purple-600">{subscription?.tier_name || 'Free'}</p>
          <Link to="/dashboard/subscription" className="text-sm text-purple-600 hover:underline mt-2 inline-block">
            View Details →
          </Link>
        </div>
      </div>

      {/* Recent Games */}
      <div className="bg-white rounded-xl shadow-md p-6">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-xl font-bold text-gray-800">Recent Games</h2>
          <Link to="/dashboard/history" className="text-teal-600 hover:underline">
            View All →
          </Link>
        </div>

        {recentGames.length === 0 ? (
          <p className="text-gray-500 text-center py-8">No games played yet</p>
        ) : (
          <div className="space-y-3">
            {recentGames.map((game) => (
              <div
                key={game.id}
                className="flex items-center justify-between p-4 border border-gray-200 rounded-lg hover:bg-gray-50 transition"
              >
                <div>
                  <h3 className="font-semibold text-gray-800">{game.game_title}</h3>
                  <p className="text-sm text-gray-600">
                    {game.venue_name} • {new Date(game.played_at).toLocaleDateString()}
                  </p>
                </div>
                <div className="text-right">
                  <p className="text-sm text-gray-600">Winner</p>
                  <p className="font-semibold text-teal-600">{game.winner_team_name}</p>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Quick Actions */}
      <div className="mt-8 grid grid-cols-1 md:grid-cols-2 gap-6">
        <Link
          to="/dashboard/venues"
          className="bg-teal-600 hover:bg-teal-700 text-white rounded-xl p-6 transition shadow-md"
        >
          <h3 className="text-xl font-bold mb-2">Create New Venue</h3>
          <p className="text-teal-100">Add a new location for hosting trivia nights</p>
        </Link>

        <Link
          to="/dashboard/templates"
          className="bg-orange-500 hover:bg-orange-600 text-white rounded-xl p-6 transition shadow-md"
        >
          <h3 className="text-xl font-bold mb-2">Browse Game Templates</h3>
          <p className="text-orange-100">Choose from our library of trivia games</p>
        </Link>
      </div>
    </div>
  );
}
